import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  toggleAddModal,
  setPage,
  setMaxPages,
  setSorting,
  setCheckedItems,
  setAddEventMode,
  setModalViewActualTab,
  toggleNeedRefresh,
  setFilterQuery,
} from '../../store/eventsSlice';
import {
  getHeaders,
  headerWidths,
  tabsConfig,
  buttonsConfig,
  leftSideConfig,
  rightSideConfig,
  empty,
} from './eventsTableConfig.js';
import { fetchUsers } from './eventsTableService.js';
import { fetchActiveAndScheduledPromotions } from './eventPromotionLookup.js';
import Table from '../../components/list2/table';
import Pagination from '../../components/components/pagination/pagination.jsx';
import Menu from '../../components/menu/menu';
import Content from '../../components/content/content';
import LineWithButtons from '../../components/components/line_with_buttons/lineWithButtons.jsx';
import UniversalLineWithComponents from '../../components/components/universal_line_with_components/universalLineWithComponents.jsx';
import ApiService from '../../components/api_services/apiService';
import AddEvent from './addEvent';
import './Events.css';

const EVENT_FILTER_OPTIONS = [
  { key: 'ACTIVE', label: 'Aktywne' },
  { key: 'NOTACTIVE', label: 'Nieaktywne' },
  { key: 'PENDING', label: 'Oczekujące' },
  { key: 'PENDING_EDITS', label: 'W trakcie zatwierdzania' },
  { key: 'ALL', label: 'Wszystkie' },
];

const getFilterToneClass = (key) => {
  if (key === 'ALL') {
    return 'event-status--all';
  }
  if (key === 'ACTIVE') {
    return 'event-status--active';
  }
  if (key === 'PENDING') {
    return 'event-status--pending';
  }
  if (key === 'PENDING_EDITS') {
    return 'event-status--data-change';
  }
  return 'event-status--inactive';
};

function EventStatusToggle({ value, onChange }) {
  const currentIndex = EVENT_FILTER_OPTIONS.findIndex((option) => option.key === value);
  const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
  const currentOption = EVENT_FILTER_OPTIONS[safeCurrentIndex];
  const nextOption = EVENT_FILTER_OPTIONS[(safeCurrentIndex + 1) % EVENT_FILTER_OPTIONS.length];

  return (
    <button
      type="button"
      className={`event-status event-status-toggle-button ${getFilterToneClass(currentOption.key)}`}
      onClick={() => onChange(nextOption.key)}
      title={`Filtr statusu: ${currentOption.label}`}
      aria-label={`Filtr statusu: ${currentOption.label}`}
    >
      {currentOption.label}
    </button>
  );
}

function Events() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const totalPages = useSelector((state) => state.events.maxPages);
  const currentPage = useSelector((state) => state.events.currentPage);
  const role = useSelector((state) => state.events.role);
  const sortBy = useSelector((state) => state.events.sortBy);
  const sortDirection = useSelector((state) => state.events.sortDirection);
  const filterQuery = useSelector((state) => state.events.filterQuery);
  const checkedItems = useSelector((state) => state.events.checkedItems);
  const needRefresh = useSelector((state) => state.events.needRefresh);
  const keyword = useSelector((state) => state.events.keyword);
  const searchValue = useSelector((state) => state.events.searchValue);
  const addModalOpen = useSelector((state) => state.events.addModalOpen);
  const editModalOpen = useSelector((state) => state.events.editModalOpen);
  const editedEvent = useSelector((state) => state.events.editedEvent);

  const [rows, setRows] = useState([]);
  const hasFilterEffectRun = useRef(false);
  const sortArrowColorActive = '#060606';
  const sortArrowColorInactive = '#06060650';
  const deleteConfirmationMessage = 'Usunięte zostaną tylko te wydarzenia, na które nie zostały utworzone paczki. Pozostałe możesz jedynie deaktywować.';

  const handleDeleteEvents = useCallback(async (ids) => {
    if (!ids || ids.length === 0) {
      return;
    }

    const isConfirmed = window.confirm(deleteConfirmationMessage);
    if (!isConfirmed) {
      return;
    }

    try {
      await ApiService.deleteEvents(ids);
      dispatch(setCheckedItems([]));
      dispatch(toggleNeedRefresh());
    } catch (error) {
      console.error('Błąd usuwania wydarzeń:', error);
    }
  }, [deleteConfirmationMessage, dispatch]);

  const handleDeleteSingleEvent = useCallback((id) => {
    handleDeleteEvents([id]);
  }, [handleDeleteEvents]);

  const handleDeleteSelectedEvents = () => {
    handleDeleteEvents(checkedItems);
  };

  const handleToggleAddModal = () => {
    dispatch(toggleAddModal());
  };

  const addModalSetTrue = () => {
    dispatch(setAddEventMode(true));
    dispatch(setModalViewActualTab('Dane podstawowe'));
  };

  useEffect(() => {
    const incomingFilter = location.state?.filterQuery;
    if (!incomingFilter) {
      return;
    }

    dispatch(setFilterQuery(incomingFilter));
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, dispatch, navigate]);

  useEffect(() => {
    if (!hasFilterEffectRun.current) {
      hasFilterEffectRun.current = true;
      return;
    }
    dispatch(setPage(1));
  }, [dispatch, filterQuery]);

  useEffect(() => {
    const loadEvents = async () => {
      const tokensValid = await ApiService.checkTokenExpiration();
      if (!tokensValid) {
        return;
      }
      const promotionMap = await fetchActiveAndScheduledPromotions();
      await fetchUsers(
        sortBy,
        sortDirection,
        currentPage,
        dispatch,
        setMaxPages,
        setRows,
        checkedItems,
        keyword,
        filterQuery,
        handleDeleteSingleEvent,
        editedEvent,
        editModalOpen,
        promotionMap
      );
    };
    loadEvents();
  }, [currentPage, role, sortBy, sortDirection, checkedItems, keyword, filterQuery, needRefresh, editModalOpen, editedEvent, dispatch, handleDeleteSingleEvent]);

  const headers = getHeaders(
    sortBy,
    sortDirection,
    dispatch,
    setSorting,
    sortArrowColorActive,
    sortArrowColorInactive,
    rows,
    checkedItems
  );

  const paginationProps = {
    reduxSelector: (state) => state.events.currentPage,
    reduxAction: setPage,
    totalPages,
    maxVisibleButtons: 5,
  };

  const searchRightSide = [
    {
      component: EventStatusToggle,
      props: {
        value: filterQuery,
        onChange: (nextFilter) => dispatch(setFilterQuery(nextFilter)),
      },
    },
    ...rightSideConfig(searchValue, dispatch),
  ];

  const rightSideConfig2 = [
    {
      component: Pagination,
      props: paginationProps,
    },
  ];

  return (
    <div>
      {(addModalOpen || editModalOpen) && <AddEvent />}
      <Menu />
      <Content>
        <LineWithButtons tabs={tabsConfig()} buttons={buttonsConfig(handleToggleAddModal, addModalSetTrue)} />
        <div className="table-container">
          <UniversalLineWithComponents leftSide={empty} rightSide={searchRightSide} />
          <Table
            headers={headers}
            rows={rows}
            columnWidths={headerWidths}
            idViewing={false}
            idArrayId={1}
            cellClass2="cell-actions"
            cellClass2ConditionList={[2]}
          />
          <UniversalLineWithComponents
            leftSide={leftSideConfig(handleDeleteSelectedEvents, checkedItems)}
            rightSide={rightSideConfig2}
          />
        </div>
      </Content>
    </div>
  );
}

export default Events;
